import React, { useEffect, useRef, useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { DownloadIcon } from '../../../Constants';
import ActionTooltip from '../ActionTooltip';

export async function downloadCapFiles(result) {
  const bytes = (content) => Uint8Array.from(atob(content), (character) => character.charCodeAt(0));
  if (result.files.length === 1) {
    const file = result.files[0];
    saveAs(new Blob([bytes(file.contentBase64)], { type: 'text/plain;charset=utf-8' }), file.filename);
  } else {
    const zip = new JSZip();
    result.files.forEach((file) => zip.file(file.filename, bytes(file.contentBase64)));
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `pcnaevents_${result.eventId}_${Math.floor(new Date(result.generatedAt).getTime() / 1000)}_cap.zip`);
  }
}

export default function GenerateCapFile({ role, eventId, buttonClassName = '' }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const currentEvent = useRef(eventId);
  const inFlight = useRef(false);
  useEffect(() => {
    currentEvent.current = eventId;
    setMessage(''); setError('');
    return () => { currentEvent.current = null; };
  }, [eventId]);
  if (role !== 1) return null;
  const generate = async () => {
    if (inFlight.current || !eventId) return;
    inFlight.current = true; setBusy(true); setMessage(''); setError('');
    try {
      const call = firebase.functions().httpsCallable('appV2/generateCapFile', { timeout: 540000 });
      const { data } = await call({ eventId });
      if (currentEvent.current !== eventId) return;
      const reasons = { disabled: 'lead transmission disabled', missingIdentity: 'missing name or email',
        testName: 'test-name records', testOrStaff: 'test/staff records', missingRouting: 'missing dealer or postal code', statusNotDue: 'ineligible registration status' };
      const excluded = Object.entries(data.excluded || {}).filter(([, count]) => count > 0)
        .map(([reason, count]) => `${count} ${reasons[reason] || 'ineligible records'}`).join('; ');
      const exclusionMessage = excluded ? ` Excluded: ${excluded}.` : '';
      if (!data.files.length) {
        setMessage(`No eligible registrations to download. Checked ${data.scanned} registrations using the manual C@P download rules.${exclusionMessage}`);
        return;
      }
      await downloadCapFiles(data);
      if (currentEvent.current === eventId) {
        setMessage(`Download started: ${data.files.map((file) => `${file.recordCount} ${file.type === 'leads' ? 'Lead' : 'Interaction History'} ${file.recordCount === 1 ? 'record' : 'records'}`).join(' and ')}${data.files.length > 1 ? ' in a ZIP file' : ''}.${exclusionMessage}`);
      }
    } catch (failure) {
      if (currentEvent.current === eventId) setError(failure.message || 'Could not generate the C@P file. Please try again.');
    } finally {
      inFlight.current = false;
      if (currentEvent.current !== null) setBusy(false);
    }
  };
  return <>
    <ActionTooltip description="Download eligible Lead and Interaction History records for the entire event. Both types download together in a ZIP when both have records; empty types are omitted. Includes core12.com and porsche.us addresses, previously sent registrations, and sessions that have not ended. Table filters do not affect the download. No email or SFTP is sent, and sent flags stay unchanged.">
      <button type="button" className={buttonClassName} disabled={busy || !eventId} onClick={generate}>
        <i className="sm shrink-0" aria-hidden="true"><DownloadIcon /></i>{' '}{busy ? 'Generating C@P…' : 'Generate C@P'}
      </button>
    </ActionTooltip>
    {busy && <p role="status" className="order-last basis-full text-sm">Reading current registrations and generating your download…</p>}
    {message && <p role="status" className="order-last basis-full text-sm">{message}</p>}
    {error && <p role="alert" className="order-last basis-full text-sm text-red-700 dark:text-red-400">{error}</p>}
  </>;
}
